import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICommodityStateFormValues } from '../../../../../@types/model/masterData/commodityState/commodityStateFormValues';
import CommodityStateModelHelper from '../../../../../@types/model/masterData/commodityState/commodityStateModelHelper';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface ICommodityStateFormProps {
    isLoading : boolean;
    initialValues : ICommodityStateFormValues;
    onSubmit : (values : ICommodityStateFormValues) => void;
    onClose : () => void;
}

const CommodityStateForm = (props : ICommodityStateFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<ICommodityStateFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(CommodityStateModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    React.useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities.filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id }));
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Name'}
                        name={'name'}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        control={control}
                        label={'Commodities'}
                        defaultValues={props.initialValues.commodities}
                        options={commodityOptions}
                        isMulti
                        name={'commodities'}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default CommodityStateForm;
